import { Middleware, PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { ShopAllowances, fetchShopAllowances } from './shop-allowances';
import { fetchShopCatalog } from './shop-catalog';
import { ShopCheckoutError, ShopEligibilityError, submitShopBasket } from './shop-checkout';
import { emptyShopDraft, writeShopDraft } from './shop-draft';
import { ShopCatalog, ShopDraft, ShopProduct, ShopScope, shopScopeKey } from './shop.types';

export interface ShopState {
    scope: ShopScope | null;
    draft: ShopDraft;
    persistenceAvailable: boolean;
    checkoutStatus: 'idle' | 'submitting' | 'success' | 'error';
    checkoutError: ShopCheckoutError | null;
    checkoutRequests: Record<string, string>;
    catalog: ShopCatalog | null;
    catalogRequestId: string | null;
    catalogStatus: 'idle' | 'loading' | 'success' | 'error';
    allowances: ShopAllowances | null;
    allowanceRequestId: string | null;
    allowanceStatus: 'idle' | 'loading' | 'success' | 'error';
}

const initialState: ShopState = {
    scope: null,
    draft: emptyShopDraft(),
    persistenceAvailable: true,
    checkoutStatus: 'idle',
    checkoutError: null,
    checkoutRequests: {},
    catalog: null,
    catalogRequestId: null,
    catalogStatus: 'idle',
    allowances: null,
    allowanceRequestId: null,
    allowanceStatus: 'idle'
};

const sameScope = (state: ShopState, scope: ShopScope) => state.scope !== null && shopScopeKey(state.scope) === shopScopeKey(scope);
export const shopBasketLocked = (state: ShopState) => state.checkoutStatus === 'submitting' || state.draft.checkoutPending;

export const loadShopCatalog = createAsyncThunk<ShopCatalog, { scope: ShopScope; path: string[] }>('shop/loadCatalog', ({ scope, path }, { signal }) =>
    fetchShopCatalog(scope, path, signal)
);

export const loadShopAllowances = createAsyncThunk<ShopAllowances, { scope: ShopScope; catalog: ShopCatalog }>(
    'shop/loadAllowances',
    async ({ catalog }) => await fetchShopAllowances(catalog)
);

export const checkoutShopBasket = createAsyncThunk<void, ShopScope, { state: { shop: ShopState }; rejectValue: ShopCheckoutError }>(
    'shop/checkout',
    async (scope, { getState, dispatch, rejectWithValue }) => {
        let draft = getState().shop.draft;
        try {
            await submitShopBasket(scope, draft, (basketId, checkoutPending) => {
                draft = { ...draft, basketId, checkoutPending };
                if (sameScope(getState().shop, scope)) dispatch(checkoutProgress({ scope, basketId, checkoutPending }));
                else writeShopDraft(scope, draft);
            });
            if (!sameScope(getState().shop, scope)) writeShopDraft(scope, { ...emptyShopDraft(), spotId: draft.spotId });
        } catch (error) {
            return rejectWithValue(error instanceof ShopEligibilityError ? 'eligibility' : 'checkout');
        }
    },
    { condition: (scope, { getState }) => sameScope(getState().shop, scope) && !getState().shop.checkoutRequests[shopScopeKey(scope)] }
);

const shopSlice = createSlice({
    name: 'shop',
    initialState,
    reducers: {
        initializeShop: (state, action: PayloadAction<{ scope: ShopScope; draft: ShopDraft }>) => {
            if (sameScope(state, action.payload.scope)) return;
            return {
                ...initialState,
                ...action.payload,
                checkoutRequests: state.checkoutRequests,
                checkoutStatus: state.checkoutRequests[shopScopeKey(action.payload.scope)] ? 'submitting' : 'idle'
            };
        },
        addShopProduct: (state, action: PayloadAction<ShopProduct>) => {
            if (!state.scope || shopBasketLocked(state)) return;
            const item = state.draft.items.find((item) => item.product.id === action.payload.id);
            if (item) {
                if (!Number.isSafeInteger(item.quantity + 1)) return;
                item.quantity++;
                item.product = action.payload;
            } else state.draft.items.push({ product: action.payload, quantity: 1 });
            state.checkoutStatus = 'idle';
            state.checkoutError = null;
        },
        changeShopQuantity: (state, action: PayloadAction<{ productId: string; quantity: number }>) => {
            if (shopBasketLocked(state) || !Number.isSafeInteger(action.payload.quantity) || action.payload.quantity < 0) return;
            const item = state.draft.items.find((item) => item.product.id === action.payload.productId);
            if (!item) return;
            if (action.payload.quantity === 0) state.draft.items = state.draft.items.filter((candidate) => candidate !== item);
            else item.quantity = action.payload.quantity;
            state.checkoutStatus = 'idle';
            state.checkoutError = null;
        },
        changeShopSpot: (state, action: PayloadAction<string | null>) => {
            if (shopBasketLocked(state)) return;
            state.draft.spotId = action.payload;
            state.checkoutStatus = 'idle';
            state.checkoutError = null;
        },
        changeShopCostCenter: (state, action: PayloadAction<string | null>) => {
            if (shopBasketLocked(state)) return;
            state.draft.costCenterId = action.payload;
            state.checkoutStatus = 'idle';
            state.checkoutError = null;
        },
        checkoutProgress: (state, action: PayloadAction<{ scope: ShopScope; basketId: string; checkoutPending: boolean }>) => {
            if (!sameScope(state, action.payload.scope)) return;
            state.draft.basketId = action.payload.basketId;
            state.draft.checkoutPending = action.payload.checkoutPending;
        },
        setShopPersistence: (state, action: PayloadAction<boolean>) => {
            state.persistenceAvailable = action.payload;
        }
    },
    extraReducers: (builder) =>
        builder
            .addCase(loadShopCatalog.pending, (state, action) => {
                if (!sameScope(state, action.meta.arg.scope)) return;
                state.catalogRequestId = action.meta.requestId;
                state.catalogStatus = 'loading';
                state.allowances = null;
                state.allowanceRequestId = null;
                state.allowanceStatus = 'idle';
            })
            .addCase(loadShopCatalog.fulfilled, (state, action) => {
                if (state.catalogRequestId !== action.meta.requestId) return;
                state.catalog = action.payload;
                state.catalogStatus = 'success';
            })
            .addCase(loadShopCatalog.rejected, (state, action) => {
                if (state.catalogRequestId !== action.meta.requestId || action.meta.aborted || action.error?.message === 'Shop request cancelled') return;
                state.catalogStatus = 'error';
            })
            .addCase(loadShopAllowances.pending, (state, action) => {
                if (!sameScope(state, action.meta.arg.scope)) return;
                state.allowanceRequestId = action.meta.requestId;
                state.allowanceStatus = 'loading';
                state.allowances = null;
            })
            .addCase(loadShopAllowances.fulfilled, (state, action) => {
                if (state.allowanceRequestId !== action.meta.requestId) return;
                state.allowances = action.payload;
                state.allowanceStatus = 'success';
            })
            .addCase(loadShopAllowances.rejected, (state, action) => {
                if (state.allowanceRequestId !== action.meta.requestId) return;
                state.allowanceStatus = 'error';
            })
            .addCase(checkoutShopBasket.pending, (state, action) => {
                state.checkoutRequests[shopScopeKey(action.meta.arg)] = action.meta.requestId;
                state.checkoutStatus = 'submitting';
                state.checkoutError = null;
            })
            .addCase(checkoutShopBasket.fulfilled, (state, action) => {
                delete state.checkoutRequests[shopScopeKey(action.meta.arg)];
                if (!sameScope(state, action.meta.arg)) return;
                state.draft = { ...emptyShopDraft(), spotId: state.draft.spotId, costCenterId: state.draft.costCenterId };
                state.checkoutStatus = 'success';
            })
            .addCase(checkoutShopBasket.rejected, (state, action) => {
                delete state.checkoutRequests[shopScopeKey(action.meta.arg)];
                if (!sameScope(state, action.meta.arg)) return;
                state.checkoutStatus = 'error';
                state.checkoutError = action.payload ?? 'checkout';
            })
});

export const { initializeShop, addShopProduct, changeShopQuantity, changeShopSpot, changeShopCostCenter, checkoutProgress, setShopPersistence } =
    shopSlice.actions;
export const shopReducer = shopSlice.reducer;

// Persist synchronously after each draft change, including remote basket IDs, so
// refreshing during a request can resume the same basket rather than create a duplicate.
export const shopPersistenceMiddleware: Middleware<object, { shop: ShopState }> = (store) => (next) => (action) => {
    const before = store.getState().shop;
    const result = next(action);
    const after = store.getState().shop;
    if (after.scope && (before.draft !== after.draft || before.scope !== after.scope)) {
        const available = writeShopDraft(after.scope, after.draft);
        if (available !== after.persistenceAvailable) store.dispatch(setShopPersistence(available));
    }
    return result;
};
